import { api } from "wasp/client/api";

/**
 * Capture et persistance du code d'affiliation (?ref=CODE).
 *
 * Transport de l'attribution avec Wasp Email Auth : localStorage (avec
 * expiration explicite 60 jours) → champ `ambassadorRef` envoyé au signup →
 * résolu côté serveur dans onAfterEmailVerified. Un cookie lu côté serveur
 * n'est pas possible : les hooks d'auth Wasp ne reçoivent pas la requête.
 * Dernier clic gagne : on écrase systématiquement la valeur stockée.
 */
const REF_KEY = "edgely_ambassador_ref";
const REF_EXP_KEY = "edgely_ambassador_ref_exp";
const REF_TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 jours

export function storeAffiliateRef(code: string): void {
  try {
    localStorage.setItem(REF_KEY, code);
    localStorage.setItem(REF_EXP_KEY, String(Date.now() + REF_TTL_MS));
  } catch {
    // localStorage indisponible (navigation privée stricte) : tant pis, pas bloquant
  }
}

export function getStoredAffiliateRef(): string | null {
  try {
    const code = localStorage.getItem(REF_KEY);
    if (!code) return null;
    const exp = Number(localStorage.getItem(REF_EXP_KEY));
    // Les valeurs posées avant l'ajout de l'expiration n'ont pas de _exp : on
    // les considère encore valides plutôt que de perdre l'attribution.
    if (exp && Date.now() > exp) {
      clearAffiliateRef();
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

export function clearAffiliateRef(): void {
  try {
    localStorage.removeItem(REF_KEY);
    localStorage.removeItem(REF_EXP_KEY);
  } catch {
    // ignore
  }
}

/**
 * À appeler au chargement de l'app : capture ?ref=CODE, logue le clic côté
 * serveur (fire-and-forget, la réponse ne révèle jamais la validité du code)
 * et nettoie le paramètre de l'URL.
 */
export function captureAffiliateRefFromUrl(): void {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("ref");
  if (!code) return;

  storeAffiliateRef(code);

  api
    .post("/affiliate/track", { code, landingPath: url.pathname })
    .catch(() => {
      // Silencieux : le tracking ne doit jamais impacter l'utilisateur
    });

  url.searchParams.delete("ref");
  window.history.replaceState({}, "", url.pathname + url.search + url.hash);
}
